import BaseModal from 'components/Modals/BaseModal';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import RoadDefinerMap from 'features/integrations/components/RoadDefinerMap.jsx';
import {
  selectIntegrations,
  selectSystemCount,
} from 'features/integrations/state/selectors/integrationSelectors';
import { useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useFusionGetIntegrationsQuery } from 'services/integrations/integrations.api';
import { TryParseString } from 'utils/parserUtils';
import ModalDropdowns from './components/ModalDropdowns';
import { getLayout, isLimitReached } from './utils/deviceModalUtils';

//TODO: Pending figma model update

/**
 * @param {onSubmit: (value: object), onCancel: (), device: object, message: {type: 'error' | 'message', message: string}, sending: boolean}
 */
export default function DeviceModal({ onSubmit, onCancel, device, message, loading }) {
  const { data: integrations = [] } = useFusionGetIntegrationsQuery();
  const systemCount = useSelector(selectSystemCount);
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const [formData, setFormData] = useState(
    device
      ? (() => {
          return {
            ...device,
            system_type: '-1',
            device_type: '-1',
            selected_device: device,
            new_node_id: device?.node_id ?? -1,
          };
        })()
      : {},
  );

  const [deviceTypes, setDeviceTypes] = useState([]);

  // Memoize layout computation to avoid repeatedly calling `getLayout`
  // on every render — it only needs to change when formData, integrations,
  // or deviceTypes change.
  const layoutConfig = useMemo(
    () => getLayout(formData, integrations, deviceTypes),
    [formData?.device_type, formData?.system_type, integrations, deviceTypes],
  );

  const coordinateMapComponents = Array.isArray(layoutConfig?.components)
    ? layoutConfig.components.filter(
        (component) => component && component.type === 'coordinate-map',
      )
    : [];
  const hasCoordinateMap = coordinateMapComponents.length > 0;

  const canSubmit = () => {
    const layoutItem = getLayout() || null;
    //if manual road closure
    if (
      formData?.device_type ===
      deviceTypes[parseInt(formData.system_type)]?.device_types.findIndex(
        (type) => type.type === 'Manual Road Closure',
      ) +
        ''
    ) {
      if (formData?.coordinates) {
        const parsedCoords = TryParseString(formData?.coordinates);
        if (
          !parsedCoords ||
          !parsedCoords?.length ||
          !parsedCoords[0] ||
          !parsedCoords[0].coordinates ||
          !parsedCoords[0].coordinates.length
        ) {
          return false;
        }
      } else {
        // console.error("Invalid coordinates", formData);
        return false;
      }
    }

    if (!device && isLimitReached(formData, connectedDevices, systemCount)) return false;

    if (layoutItem) {
      return !['system_type', 'device_type', ...(layoutItem['required-fields'] || [])]
        .map((key) => (key === 'device_type' ? formData[key] >= 0 : !!formData[key]))
        .includes(false);
    } else {
      return !['system_type', 'device_type']
        .map((key) => (key === 'device_type' ? formData[key] >= 0 : !!formData[key]))
        .includes(false);
    }
  };

  // convert formdata to device object on submit
  const submit = () => {
    if (isLimitReached(formData, connectedDevices, systemCount)) {
      console.warn(`Device cap reached for selected device cannot submit.`);
      return;
  }

    // Resolve system_type and device_type from indices to actual type strings
    const integrationIdx = Number(formData.system_type);
    const selectedIntegration = integrations?.[integrationIdx];

    // Find system index in deviceTypes
    const sysIdx = selectedIntegration
      ? deviceTypes.findIndex((sys) => sys.system_type === selectedIntegration.system_type)
      : -1;

    const deviceTypeIdx = Number(formData.device_type);

    const resolvedSystemType =
      sysIdx >= 0
        ? deviceTypes[sysIdx].system_type
        : (selectedIntegration?.system_type ?? deviceTypes[integrationIdx]?.system_type);

    const resolvedDeviceType =
      sysIdx >= 0
        ? deviceTypes[sysIdx]?.device_types?.[deviceTypeIdx]?.type
        : deviceTypes[integrationIdx]?.device_types?.[deviceTypeIdx]?.type;

    // If alias was left blank, fall back to the selected station's name so it is
    // never stored as null/empty — consistent with the Station Name fallback requirement.
    const resolvedAlias = formData.alias?.trim() || formData.device?.name || '';

    onSubmit({
      ...formData,
      alias: resolvedAlias,
      system_type: resolvedSystemType,
      device_type: resolvedDeviceType,
    });
  };

  return (
    <BaseModal
      show={true}
      size={device ? 'xl' : 'lg'}
      handleShow={onCancel}
      title={device ? 'Edit Device' : 'New Device'}
    >
      <Form className="d-flex flex-column flex-xl-row gap-3">
        {/* Left Section */}
        <div className="d-flex flex-column w-100">
          <ModalDropdowns
            deviceTypes={deviceTypes}
            formData={formData}
            setFormData={setFormData}
            setDeviceTypes={setDeviceTypes}
            device={device}
            layoutConfig={layoutConfig}
          />

          {onSubmit && (
            <div className="d-flex justify-content-sm-end justify-content-center mb-3">
              <Button
                disabled={!canSubmit()}
                className="border-0 mt-3 py-2 px-4 rounded-4 w-100 w-sm-auto"
                onClick={submit}
              >
                {device ? 'Submit' : 'Connect'}
              </Button>
            </div>
          )}
        </div>

        {/* Right Section */}
        {hasCoordinateMap && (
          <div className="w-100">
            {coordinateMapComponents.map((component, index) => (
              <RoadDefinerMap
                key={component['data-field'] || index}
                value={TryParseString(formData[component['data-field']])}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    [component['data-field']]: value,
                  })
                }
                initialCenter={device?.location ? device.location : null}
              />
            ))}
          </div>
        )}
      </Form>

      {message && (
        <p className="response-message" data-type={message.type}>
          {message.message}
        </p>
      )}
      {loading && (
        <div>
          <WidgetLoadingAnimation disableText />
        </div>
      )}
    </BaseModal>
  );
}
